import { EVENT_ROUTES, ROUTES } from 'constants/routes';

/**
 * Where a legacy `/event/:eventId/...` URL lands in the v2 event experience.
 *
 * The legacy event pages are gone, but their URLs are still in circulation: the API's
 * emails link to `/event/:id`, `/event/:id/venue/inquiries[/:inquiryId]` and
 * `/event/:id/venue/proposal/:proposalId`, and planners have old ones bookmarked.
 *
 * A v2 proposal URL needs the venue ID, which a v1 proposal URL doesn't carry, so those
 * resolve through the API (`proposal`). Everything else maps directly (`path`).
 */
export type V1Redirect =
    { type: 'path'; to: string } | { type: 'proposal'; eventId: string; proposalId: string; messages: boolean };

const V1_EVENT = /^\/event\/(\d+)(?:\/(.*?))?\/?$/;
const V1_PROPOSAL = /^venue\/proposal\/(\d+)(\/messages)?(?:\/.*)?$/;

// v1 sections whose v2 page has the same path segment
const SAME_NAME_SECTIONS: string[] = [
    EVENT_ROUTES.SPEND_TRACKER,
    EVENT_ROUTES.GUESTS,
    EVENT_ROUTES.COLLABORATORS,
    EVENT_ROUTES.RESOURCES,
];

export const getV1Redirect = (pathname: string, search = ''): V1Redirect | null => {
    const match = pathname.match(V1_EVENT);
    if (!match) return null;

    const [, eventId, rest = ''] = match;
    const eventBase = `${ROUTES.EVENT}/${eventId}`;

    const proposal = rest.match(V1_PROPOSAL);
    if (proposal) {
        return { type: 'proposal', eventId, proposalId: proposal[1], messages: !!proposal[2] };
    }

    const [section, subsection] = rest.split('/');

    if (section === 'venue') {
        // `/venue` alone was venue search; inquiries (list, draft or a specific one) now
        // live on the venues overview, per venue.
        if (!subsection) return { type: 'path', to: `${eventBase}/${EVENT_ROUTES.VENUES_SEARCH}` };
        if (subsection === 'inquiries') return { type: 'path', to: `${eventBase}/${EVENT_ROUTES.VENUES_OVERVIEW}` };
        if (subsection === 'contracting') return { type: 'path', to: `${eventBase}/${EVENT_ROUTES.ACCEPTED_VENUE}` };
    }

    if (SAME_NAME_SECTIONS.includes(section)) {
        // Keep the query — spend tracker links carry `?tab=estimate|final`
        return { type: 'path', to: `${eventBase}/${section}${search}` };
    }

    // Overview, planner, agenda and anything unrecognised land on the event itself
    return { type: 'path', to: eventBase };
};
